import { message as antdMessage } from 'antd';
import axios, { AxiosError } from 'axios';
import get from 'lodash/get';
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';

import HttpStatusCode from 'types/http-status-code.enum';
import BackOfficeInvitation from 'types/models/back-office-invitation';
import Enterprise, { EnterpriseId } from 'types/models/enterprise';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'utils/redux-saga-requests';

import {
  acceptInvitation,
  acceptInvitationFailure,
  acceptInvitationSuccess,
  addInvitation,
  addInvitationFailure,
  addInvitationSuccess,
  deleteInvitation,
  deleteInvitationFailure,
  deleteInvitationSuccess,
  loadCurrentEnterpriseInvitationsFailure,
  loadCurrentEnterpriseInvitationsSuccess,
  loadInvitation,
  loadInvitationFailure,
  loadInvitationSuccess,
  loadMyInvitationsFailure,
  loadMyInvitationsSuccess,
  resendInvitation,
  resendInvitationFailure,
  resendInvitationSuccess,
  updateInvitation,
  updateInvitationFailure,
  updateInvitationSuccess
} from './actions';
import ActionTypes from './constants';
import { setEnterprise } from '../enterprise/actions';
import { selectCurrentEnterpriseId } from '../enterprise/selectors';
import { mergeMyUser } from '../myUser/actions';
import trackEvent from '../tracking/actions';
import { EventType } from '../tracking/types';

function* requestLoadCurrentEnterpriseInvitations() {
  try {
    const enterpriseId: EnterpriseId = yield select(selectCurrentEnterpriseId);

    const { data } = yield call(getRequest, 'invitation', {
      params: {
        enterpriseId
      }
    });

    yield put(loadCurrentEnterpriseInvitationsSuccess(data));
  } catch (error) {
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    yield put(
      loadCurrentEnterpriseInvitationsFailure(message, error as AxiosError)
    );
  }
}

function* requestLoadMyInvitations() {
  try {
    const { data }: { data: BackOfficeInvitation[] } = yield call(
      getRequest,
      'invitation/mine'
    );
    yield put(loadMyInvitationsSuccess());
    yield put(mergeMyUser({ invitations: data }));
  } catch (error) {
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    yield put(loadMyInvitationsFailure(message, error as AxiosError));
  }
}

function* requestLoadInvitation({
  payload: { invitationSid, onSuccess }
}: ReturnType<typeof loadInvitation>) {
  try {
    const { data } = yield call(getRequest, `invitation/${invitationSid}`);
    data.isNewUser = data.isNewCustomer;
    delete data.isNewCustomer;
    onSuccess(data);
    // Only set enterprise if it exists; invitations for internal admin roles will not have an enterprise.
    // setEnterprise(undefined) will throw an error, falling to the catch block and making the toast appear misleadingly.
    if (data.enterprise) {
      const phoneNumberCountryCodeHint = {
        billingAddress: {
          country: data.enterprise.billingAddress?.country
        }
      } as Enterprise;
      // The hint will be used by FormItemInputPhoneNumber via useEnterpriseCountry.
      yield put(setEnterprise(phoneNumberCountryCodeHint));
    }
    yield put(loadInvitationSuccess());
  } catch (error) {
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    antdMessage.error(
      'Could not load invitation. Please make sure it\'s not expired'
    );
    yield put(loadInvitationFailure(message, error as AxiosError));
  }
}

function* requestAddInvitation({
  payload: { newInvitation, onSuccess }
}: ReturnType<typeof addInvitation>) {
  try {
    const { data } = yield call(postRequest, 'invitation', { ...newInvitation });
    onSuccess();
    antdMessage.success('Email invitation sent');
    yield put(addInvitationSuccess(data));
    yield put(
      trackEvent({
        type: EventType.USER_INVITED,
        data: {
          inviteeId: data.sid,
          inviteeEmail: data.email,
          inviteeRole: data.role,
          inviteeIsEnterpriseOwner: data.isEnterpriseOwner,
          companyId: data.enterprise.sid,
          company: data.enterprise.name,
          entryPoint: 'Team Members Page'
        }
      })
    );
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    ).replace(/\.$/, ''); // Remove trailing period per UX rule: https://4x.ant.design/docs/spec/copywriting#Capitalization-and-punctuation
    if ((error as AxiosError).response?.status === HttpStatusCode.CONFLICT) {
      antdMessage.error(message);
    } else if (/Cannot invite yourself/i.test(message)) {
      antdMessage.error(message);
    } else {
      antdMessage.error('Could not send invitation, please try again later');
    }
    yield put(addInvitationFailure(message, error as AxiosError));
  }
}

function* requestAcceptInvitation({
  payload: { invitation, onSuccess }
}: ReturnType<typeof acceptInvitation>) {
  try {
    yield call(putRequest, `invitation/${invitation.sid}/accept`, {});
    onSuccess();
    yield put(acceptInvitationSuccess(invitation.sid));
    if (invitation.enterprise) {
      yield put(
        trackEvent({
          type: EventType.USER_JOINED_ENTERPRISE,
          data: {
            email: invitation.email,
            isEnterpriseOwner: invitation.isEnterpriseOwner,
            companyId: invitation.enterprise.sid,
            company: invitation.enterprise.name
          }
        })
      );
    }
  } catch (error) {
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    antdMessage.error('Could not accept invitation, please try again later');
    yield put(acceptInvitationFailure(message, error as AxiosError));
  }
}

function* requestResendInvitation({
  payload: { invitationSid }
}: ReturnType<typeof resendInvitation>) {
  try {
    yield call(putRequest, `invitation/${invitationSid}/resend`, {});

    antdMessage.success('Invitation resent successfully');
    yield put(resendInvitationSuccess(invitationSid));
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    if (error?.response?.status === HttpStatusCode.TOO_MANY_REQUESTS) {
      antdMessage.warning(message);
    } else {
      antdMessage.error('Could not resend invitation, please try again later');
    }
    yield put(resendInvitationFailure(message, error));
  }
}

function* requestDeleteInvitation({
  payload: { invitationSid, onSuccess }
}: ReturnType<typeof deleteInvitation>) {
  try {
    yield call(deleteRequest, `invitation/${invitationSid}`);
    onSuccess();
    yield put(deleteInvitationSuccess(invitationSid));
  } catch (error) {
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    antdMessage.error('Could not delete invitation, please try again later');
    yield put(deleteInvitationFailure(message, error as AxiosError));
  }
}

function* requestUpdateInvitation({
  payload: { sid, updatedInvitation }
}: ReturnType<typeof updateInvitation>) {
  try {
    yield call(putRequest, `invitation/${sid}`, updatedInvitation);

    antdMessage.success('Invitation updated successfully');
    yield put(updateInvitationSuccess(sid, updatedInvitation));
  } catch (error) {
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    antdMessage.error('Could not update invitation, please try again later');
    yield put(updateInvitationFailure(message, error as AxiosError));
  }
}

function* watchLoadCurrentEnterpriseInvitationsRequest() {
  yield takeLatest(
    ActionTypes.LOAD_CURRENT_ENTERPRISE_INVITATIONS_REQUEST,
    requestLoadCurrentEnterpriseInvitations
  );
}

function* watchLoadMyInvitationsRequest() {
  yield takeLatest(
    ActionTypes.LOAD_MY_INVITATIONS_REQUEST,
    requestLoadMyInvitations
  );
}

function* watchLoadInvitationRequest() {
  yield takeLatest(ActionTypes.LOAD_INVITATION_REQUEST, requestLoadInvitation);
}

function* watchAddInvitationRequest() {
  yield takeLatest(ActionTypes.ADD_INVITATION_REQUEST, requestAddInvitation);
}

function* watchAcceptInvitationRequest() {
  yield takeLatest(
    ActionTypes.ACCEPT_INVITATION_REQUEST,
    requestAcceptInvitation
  );
}

function* watchResendInvitationRequest() {
  yield takeLatest(
    ActionTypes.RESEND_INVITATION_REQUEST,
    requestResendInvitation
  );
}

function* watchDeleteInvitationRequest() {
  yield takeLatest(
    ActionTypes.DELETE_INVITATION_REQUEST,
    requestDeleteInvitation
  );
}

function* watchUpdateInvitationRequest() {
  yield takeLatest(
    ActionTypes.UPDATE_INVITATION_REQUEST,
    requestUpdateInvitation
  );
}

export default function* invitationSaga() {
  yield all([
    fork(watchLoadCurrentEnterpriseInvitationsRequest),
    fork(watchLoadMyInvitationsRequest),
    fork(watchLoadInvitationRequest),
    fork(watchAddInvitationRequest),
    fork(watchAcceptInvitationRequest),
    fork(watchResendInvitationRequest),
    fork(watchDeleteInvitationRequest),
    fork(watchUpdateInvitationRequest)
  ]);
}
